import { DefaultProjectorFn, MemoizedSelector, createSelector } from '@ngrx/store';
import { AppState } from '../app.state';
import {
  PaidFeature,
  PaidFeatureScope,
  SubscriptionPlan,
  SubscriptionWithPaidFeatures,
} from '../../../projects/client';
import { DemoSettingsState } from './demo-settings.state';

export namespace DemoSettingsSelectors {
  export const demoSettingsState = (state: AppState): DemoSettingsState => state.demoSettings;

  export const selectSubscriptionWithPaidFeatures = createSelector(
    demoSettingsState,
    (state): SubscriptionWithPaidFeatures[] | null => state.subscriptionWithPaidFeatures
  );

  export function selectAreAllFeaturesEnabled(
    resourceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      demoSettingsState,
      (demoSettings): boolean =>
        demoSettings.subscriptionWithPaidFeatures.find(
          (subscriptionWithPaidFeatures) => subscriptionWithPaidFeatures.resourceId === resourceId
        ).features.length === Object.keys(PaidFeature).length
    );
  }

  export function selectIsFeatureEnabled(
    resourceId: string,
    paidFeatureKey: PaidFeature
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      demoSettingsState,
      (demoSettings): boolean =>
        !!demoSettings.subscriptionWithPaidFeatures
          .find(
            (subscriptionWithPaidFeatures) => subscriptionWithPaidFeatures.resourceId === resourceId
          )
          .features.find((feature) => feature === paidFeatureKey)
    );
  }

  export function selectCurrentPlan(
    resourceId: string
  ): MemoizedSelector<AppState, SubscriptionPlan, DefaultProjectorFn<SubscriptionPlan>> {
    return createSelector(
      demoSettingsState,
      (demoSettings): SubscriptionPlan =>
        demoSettings.subscriptionWithPaidFeatures.find(
          (subscriptionWithPaidFeatures) => subscriptionWithPaidFeatures.resourceId === resourceId
        ).plan
    );
  }

  export function getSubscriptionForResourceId(
    resourceId: string
  ): MemoizedSelector<
    AppState,
    SubscriptionWithPaidFeatures[],
    DefaultProjectorFn<SubscriptionWithPaidFeatures[]>
  > {
    return createSelector(demoSettingsState, (demoSettings):
      | SubscriptionWithPaidFeatures[]
      | undefined => {
      const subscription = demoSettings.subscriptionWithPaidFeatures.find(
        (subscriptionWithPaidFeatures) => subscriptionWithPaidFeatures.resourceId === resourceId
      );

      if (subscription) {
        return [subscription];
      } else {
        return [
          {
            plan: SubscriptionPlan.Free,
            scope: PaidFeatureScope.company,
            features: [
              PaidFeature.Analytics,
              PaidFeature.Dashboard,
              PaidFeature.Equipment,
              PaidFeature.EventHistory,
              PaidFeature.Features,
              PaidFeature.LiveUpdates,
              PaidFeature.Mails,
              PaidFeature.Monetization,
              PaidFeature.Reports,
              PaidFeature.SmartNotes,
              PaidFeature.UserManagement,
              PaidFeature.ViguideDefaultAccess,
            ],
            resourceId: '',
          },
        ];
      }
    });
  }
}
